<template>
  <div class="billing-section mt-2">
    <div class="px-2">
      <p class="billing-section-title">Billing</p>
    </div>
    <div class="billing-card">
      <div class="billing-row" v-for="row in billingRows" :key="row.label">
        <div class="billing-icon">
          <v-icon size="18" color="deep-orange-darken-1">{{ row.icon }}</v-icon>
        </div>
        <div class="billing-copy">
          <p class="billing-label">{{ row.label }}</p>
          <p class="billing-value">{{ row.value }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { formatDate } from "@/shared/utils/utils";

type SubscriptionDetailResponse = {
  recurring_subscription?: {
    billing_interval: string | null;
    start_date: string | null;
    next_billing_date: string | null;
    end_date: string | null;
  };
};

const props = defineProps<{
  detail: SubscriptionDetailResponse;
}>();

function titleCase(value: string | null | undefined) {
  const text = String(value ?? "").trim();
  if (!text) return "-";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

const billingRows = computed(() => [
  {
    label: "Billing cycle",
    value: props.detail.recurring_subscription?.billing_interval
      ? `Every 1 ${titleCase(props.detail.recurring_subscription.billing_interval)}`
      : "-",
    icon: "mdi-refresh",
  },
  {
    label: "Start date",
    value: props.detail.recurring_subscription?.start_date
      ? formatDate(props.detail.recurring_subscription.start_date)
      : "-",
    icon: "mdi-calendar-start-outline",
  },
  {
    label: "Next billing",
    value: props.detail.recurring_subscription?.next_billing_date
      ? formatDate(props.detail.recurring_subscription.next_billing_date)
      : "-",
    icon: "mdi-calendar-clock-outline",
  },
  {
    label: "End date",
    value: props.detail.recurring_subscription?.end_date
      ? formatDate(props.detail.recurring_subscription.end_date)
      : "Ongoing",
    icon: "mdi-calendar-end-outline",
  },
]);
</script>

<style scoped>
.billing-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.billing-section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #6b7280;
}

.billing-card {
  padding: 0;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
}

.billing-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #e5e7eb;
}

.billing-row:last-child {
  border-bottom: 0;
}

.billing-icon {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(249, 115, 22, 0.12);
  flex: 0 0 auto;
}

.billing-copy {
  min-width: 0;
}

.billing-label {
  margin: 0;
  font-size: 14px;
  color: var(--color-subtitle-light)
}

.billing-value {
  margin: 2px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
</style>
